import classNames from 'classnames'
import React, { FC, PropsWithChildren, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsRTL } from '@context/SessionSettingsContext'

import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useWindowResize } from '@utils/domUtils'

import { HorizontalScroll, HorizontalScrollFade } from '@global/HorizontalScroll/HorizontalScroll'

import { HorizontalScroll as ScrollProps } from '@hooks/useHorizontalScroll'

import styles from './FilterHorizontalScroll.module.scss'

const FADE_WIDTH = '2.5rem'
const SCROLL_RANGE = 250

export interface FilterHorizontalScrollProps extends PropsWithChildren {
  scrollBehavior?: 'reset' | 'preserve' | number
}

export const FilterHorizontalScroll: FC<FilterHorizontalScrollProps> = (props) => {
  const { children, scrollBehavior } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const isRTL = useIsRTL()

  const [isMouseOver, setIsMouseOver] = useState(false)

  const onNavigateLeft = useCallback(
    (scrollProps: ScrollProps) => {
      scrollProps.scrollContainerRef?.current?.scrollBy({
        left: isRTL ? SCROLL_RANGE : -SCROLL_RANGE,
        behavior: 'smooth',
      })
    },
    [isRTL]
  )

  const onNavigateRight = useCallback(
    (scrollProps: ScrollProps) => {
      scrollProps.scrollContainerRef?.current?.scrollBy({
        left: isRTL ? -SCROLL_RANGE : SCROLL_RANGE,
        behavior: 'smooth',
      })
    },
    [isRTL]
  )

  const handleMouseOver = useCallback(() => {
    setIsMouseOver(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsMouseOver(false)
  }, [])

  const renderAdditionalContent = useCallback(
    (scrollProps: ScrollProps) => {
      const { isScrollable, hasScrolledToStart, hasScrolledToEnd } = scrollProps

      if (!isScrollable || isMobile) {
        return null
      }

      const LeftButton = (
        <Tooltip content={t('Move Left')}>
          <IconButton.Secondary
            size='md'
            icon={ArrowIcon.Left}
            onClick={() => onNavigateLeft(scrollProps)}
          />
        </Tooltip>
      )

      const RightButton = (
        <Tooltip content={t('Move Right')}>
          <IconButton.Secondary
            size='md'
            icon={ArrowIcon.Right}
            onClick={() => onNavigateRight(scrollProps)}
          />
        </Tooltip>
      )

      return (
        <>
          <div
            className={classNames(styles.navigationButton, styles.left, {
              [styles.visible]: isMouseOver && !hasScrolledToStart,
            })}
          >
            {LeftButton}
            <div className={styles.fade}>
              <HorizontalScrollFade.Left style={{ width: FADE_WIDTH }} active />
            </div>
          </div>
          <div
            className={classNames(styles.navigationButton, styles.right, {
              [styles.visible]: isMouseOver && !hasScrolledToEnd,
            })}
          >
            <div className={styles.fade}>
              <HorizontalScrollFade.Right style={{ width: FADE_WIDTH }} active />
            </div>
            {RightButton}
          </div>
        </>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, isMouseOver]
  )

  return (
    <HorizontalScroll
      scrollBehavior={scrollBehavior}
      fadeWidth={FADE_WIDTH}
      shouldRestorePosition
      renderAdditionalContent={renderAdditionalContent}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      innerClassName={styles.badgesScroll}
      containerClassName={styles.badgesContainer}
      className={styles.filterHorizontalScroll}
    >
      {children}
    </HorizontalScroll>
  )
}
